import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Trash2, AlertTriangle, Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/lib/AuthContext";
import { getDeletionPreview, deleteAccount } from "@/lib/api";
import { toast } from "sonner";

export default function DeleteAccount() {
  const { user, loading: authLoading, loginWithGoogle, logout } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [ackSub, setAckSub] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (authLoading || !user) return;
    setLoadingPreview(true);
    getDeletionPreview()
      .then(setPreview)
      .catch(() => toast.error("Could not load your account details. Please try again."))
      .finally(() => setLoadingPreview(false));
  }, [user, authLoading]);

  const emailMatches = user && confirmEmail.trim().toLowerCase() === user.email.toLowerCase();
  const hasActiveSub = preview?.subscription?.active;
  const canDelete = emailMatches && (!hasActiveSub || ackSub) && !deleting;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteAccount({ confirm_email: confirmEmail.trim(), acknowledge_subscription: ackSub });
      setDone(true);
      // Clear client auth state
      try { await logout(); } catch {}
    } catch (e) {
      const msg = e?.response?.data?.detail || "Deletion failed. Please try again.";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors" data-testid="delete-back-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm tracking-wider uppercase">Home</span>
          </Link>
        </div>
      </nav>

      <main className="pt-14 max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-2 mb-1">
          <img src="/logo.png" alt="Daily Nakshatra" className="h-6 w-auto" />
          <span className="text-white/40 text-xs tracking-[0.2em] uppercase">Daily Nakshatra</span>
        </div>
        <div className="flex items-center gap-3 mb-2">
          <Trash2 className="w-6 h-6 text-red-400/80" />
          <h1 className="text-3xl sm:text-4xl font-light text-white/95" data-testid="delete-title">Delete your account</h1>
        </div>
        <p className="text-white/45 text-sm leading-7 mb-8">
          This page lets you permanently delete your Daily Nakshatra account and personal data. You can start here
          without reinstalling the app. We verify it's really you before anything is deleted.
        </p>

        {/* Success state */}
        {done ? (
          <div className="rounded-xl p-6 text-center" style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.25)" }} data-testid="delete-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <h2 className="text-xl font-light text-white/90 mb-2">Your account has been deleted</h2>
            <p className="text-white/50 text-sm leading-7">
              Your account profile, birth data, saved charts, reports and preferences have been permanently removed,
              and all your sessions have been signed out. Anonymised purchase records may be retained for legal and
              tax purposes as described in our{" "}
              <Link to="/privacy" className="text-amber-400/80 hover:text-amber-400 underline">Privacy Policy</Link>.
            </p>
            <Link to="/" className="inline-block mt-5 text-white/40 hover:text-white/70 text-sm">Return home</Link>
          </div>
        ) : authLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-white/40 animate-spin" /></div>
        ) : !user ? (
          /* Not signed in — require fresh authentication */
          <div className="rounded-xl p-6" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="delete-signin-gate">
            <div className="flex items-start gap-3 mb-4">
              <ShieldAlert className="w-5 h-5 text-amber-400/80 mt-0.5 flex-shrink-0" />
              <p className="text-white/60 text-sm leading-7">
                To protect your account, please sign in to verify ownership. Deletion can only be started by the
                account owner.
              </p>
            </div>
            <Button onClick={loginWithGoogle} className="w-full bg-white text-black hover:bg-white/90 rounded-full h-11 text-sm" data-testid="delete-signin-btn">
              Sign in to continue
            </Button>
          </div>
        ) : loadingPreview || !preview ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-white/40 animate-spin" /></div>
        ) : (
          <div className="space-y-6">
            {/* What gets deleted */}
            <div className="rounded-xl p-5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <p className="text-white/80 text-sm font-medium mb-3">What will be permanently deleted</p>
              <ul className="space-y-1.5">
                {preview.will_delete.map((item, i) => (
                  <li key={i} className="flex gap-2 text-white/55 text-sm leading-6">
                    <span className="text-red-400/60 mt-2 w-1 h-1 rounded-full bg-red-400/60 flex-shrink-0" />{item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Retained records */}
            <div className="rounded-xl p-5" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <p className="text-white/70 text-sm font-medium mb-2">What we must keep</p>
              <p className="text-white/45 text-xs leading-6 mb-2">{preview.retained.purchase_records}</p>
              <p className="text-white/45 text-xs leading-6">{preview.retained.backups}</p>
            </div>

            {/* Subscription warning */}
            {hasActiveSub && (
              <div className="rounded-xl p-5" style={{ background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.3)" }} data-testid="delete-subscription-warning">
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-amber-200/90 text-sm leading-7">
                    You have an active <span className="font-medium">{preview.subscription.type || "subscription"}</span>.
                    Deleting your account does <span className="font-medium">not</span> cancel billing. Please cancel
                    your subscription separately through the payment provider or app store where you subscribed.
                  </p>
                </div>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <Checkbox checked={ackSub} onCheckedChange={(v) => setAckSub(!!v)} className="mt-0.5" data-testid="delete-ack-subscription" />
                  <span className="text-white/60 text-xs leading-6">I understand that deleting my account will not cancel my subscription and I am responsible for cancelling it separately.</span>
                </label>
              </div>
            )}

            {/* Confirmation */}
            <div className="rounded-xl p-5" style={{ background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.25)" }}>
              <p className="text-white/80 text-sm font-medium mb-1">Confirm deletion</p>
              <p className="text-white/45 text-xs leading-6 mb-4">
                This action is permanent and cannot be undone. Type your account email{" "}
                <span className="text-white/70">{user.email}</span> to confirm.
              </p>
              <Input
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder="Type your email to confirm"
                className="bg-black/30 border-white/10 text-white placeholder:text-white/25 mb-4"
                data-testid="delete-confirm-email"
              />
              <Button
                onClick={handleDelete}
                disabled={!canDelete}
                className="w-full bg-red-500/90 text-white hover:bg-red-500 disabled:opacity-40 rounded-full h-11 text-sm font-medium"
                data-testid="delete-confirm-btn"
              >
                {deleting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…</>) : (<>Permanently delete my account</>)}
              </Button>
            </div>

            <p className="text-center text-white/30 text-xs">
              Prefer to keep your account? <Link to="/" className="text-white/50 hover:text-white/80 underline">Go back</Link> ·{" "}
              <Link to="/privacy" className="text-white/50 hover:text-white/80 underline">Privacy Policy</Link>
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
